import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    TrackedRideContract,
    VehicleType,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    LogService,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    ShowOnMapService,
    TrackedRide,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { find, get, set } from 'lodash-es';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
    DeleteTrackedRide,
    ForceTrackedRideRefresh,
    SetTrackedRides,
    UpdateTrackedRide,
} from 'src/app/components/tracked-ride/tracked-ride.actions';
import {
    selectShouldForceTrackedRideRefresh,
    selectTrackedRides,
} from 'src/app/components/tracked-ride/tracked-ride.reducer';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { UserContentColorHex } from '../types/user-content-color-hex';
import { TrackedRideResourceService } from './tracked-ride-resource.service';

@Injectable({ providedIn: 'root' })
export class TrackedRideService {
    readonly className = 'TrackedRideService';

    private trackedRides: TrackedRide[];

    constructor(
        private configService: ConfigService,
        private logService: LogService,
        private showOnMapService: ShowOnMapService,
        private store: Store<AppState>,
        private trackedRideResource: TrackedRideResourceService,
        private userAuthService: UserAuthService
    ) {
        store
            .select(selectTrackedRides)
            .subscribe((trackedRides) => (this.trackedRides = trackedRides));
    }

    //#region Get
    getTrackedRides$(): Observable<TrackedRide[]> {
        return combineLatest([
            this.store.select(selectShouldForceTrackedRideRefresh),
            this.userAuthService.isAuthenticated$,
        ]).pipe(
            switchMap(([shouldForce, isAuthenticated]) => {
                if (!isAuthenticated) {
                    return of([] as TrackedRide[]);
                }
                if (shouldForce) {
                    return this.trackedRideResource
                        .getTrackedRidesForUser()
                        .pipe(
                            map((rideCollection) => {
                                const contracts = rideCollection.features;
                                return (
                                    contracts?.map((contract) => {
                                        return new TrackedRide(
                                            contract,
                                            this.configService.appSku
                                        );
                                    }) ?? []
                                );
                            }),
                            tap((rides) => {
                                this.store.dispatch(new SetTrackedRides(rides));
                                this.store.dispatch(
                                    new ForceTrackedRideRefresh(false)
                                );
                            }),
                            catchError((error) => {
                                this.logService.error(this.className, error);
                                return of([] as TrackedRide[]);
                            })
                        );
                } else {
                    return this.store.select(selectTrackedRides);
                }
            })
        );
    }

    getTrackedRideById(
        id: string,
        forceRefresh = false
    ): Observable<TrackedRide> {
        if (!forceRefresh) {
            const ride = this.getCachedTrackedRide(id);
            if (ride) {
                return of(ride);
            }
        }

        return this.trackedRideResource.getTrackedRideById(id).pipe(
            tap((trackedRide) => {
                this.store.dispatch(new UpdateTrackedRide(trackedRide));
            })
        );
    }

    getCachedTrackedRide(id: string): TrackedRide | undefined {
        if (this.trackedRides.length === 0) {
            return undefined;
        }

        return find(this.trackedRides, { id });
    }

    downloadTrackedRideGpx(id: string): void {
        this.trackedRideResource.downloadGpx(id);
    }

    convertTrackedRideToGpx(content: FormData): Observable<null> {
        return this.trackedRideResource.convertTrackedRideToGpx(content);
    }

    convertGpxToTrackedRide(
        content: FormData
    ): Observable<TrackedRideContract> {
        return this.trackedRideResource.convertGpxToTrackedRide(content);
    }

    //#endregion

    uploadTrackedRideGpx(
        content: FormData,
        rideType: VehicleType
    ): Observable<string> {
        return this.trackedRideResource.uploadGpx(content, rideType).pipe(
            // Since we are adding ride(s) to the list, we need to ensure the rides list
            // is reloaded next time it is called.
            tap(() => {
                this.store.dispatch(new ForceTrackedRideRefresh(true));
            })
        );
    }

    updateTrackedRide(ride: TrackedRide): Observable<TrackedRide> {
        ride.updateModifiedDate();
        return this.trackedRideResource
            .updateTrackedRide(ride.toGeoJson())
            .pipe(
                switchMap(() => this.getTrackedRideById(ride.id, true)),
                tap((updatedRide) => {
                    this.store.dispatch(new UpdateTrackedRide(updatedRide));
                })
            );
    }

    deleteTrackedRide(id: string): Observable<void> {
        return this.trackedRideResource.deleteTrackedRide(id).pipe(
            tap(() => {
                this.store.dispatch(new DeleteTrackedRide(id));
            })
        );
    }

    deleteSelectedTrackedRides(
        rides: TrackedRide[]
    ): Observable<{ id: string; success: boolean }[]> {
        if (rides.length === 0) {
            return of([]);
        }

        const resultObservables = rides.map((trackedRide: TrackedRide) => {
            return this.deleteTrackedRide(trackedRide.id).pipe(
                catchError(() => {
                    return of('failed');
                }),
                map((result) => {
                    return { id: trackedRide.id, success: result !== 'failed' };
                })
            );
        });

        return forkJoin(resultObservables);
    }

    getBounds(ride: TrackedRide): GeoBounds {
        return GeoBounds.fromCoordinateArrayPath(this.getPath(ride));
    }

    getPath(ride: TrackedRide): GeoJSON.Position[] {
        return get(
            ride,
            'rawTrackedRide.geometry.coordinates',
            get(ride, 'geometry.coordinates', [])
        ) as GeoJSON.Position[];
    }

    updateFlyByUrl(id: string): Observable<string> {
        return this.trackedRideResource.updateFlyByUrl(id);
    }

    removeFromMap(ride: TrackedRide): void {
        this.showOnMapService.setShouldShow(ride.id, false);
        this.store.dispatch(new UpdateTrackedRide(ride));
    }

    showOnMap(ride: TrackedRide): void {
        this.showOnMapService.setShouldShow(ride.id, true);
        this.store.dispatch(new UpdateTrackedRide(ride));
    }

    trackedRidesToGeoJson(
        trackedRides: TrackedRide[] = []
    ): GeoJsonFeatureCollection {
        const features = trackedRides.map((ride) => {
            const feature = ride.toGeoJson();
            // TODO: separate geoJson and the DTO definitions
            if (ride.color) {
                set(
                    feature,
                    'properties.color',
                    UserContentColorHex[ride.color]
                );
            } else {
                set(feature, 'properties.color', '#0099CC');
            }
            return feature;
        });
        return new GeoJsonFeatureCollection(features);
    }

    trackedRidesToGeoJsonPoints(
        trackedRides: TrackedRide[] = []
    ): GeoJsonFeatureCollection {
        const features = trackedRides.map((ride) => ride.toGeoJsonPoint());
        return new GeoJsonFeatureCollection(features);
    }
}
